import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Link2Off, ShieldAlert } from 'lucide-react';
import { api } from '@/lib/api';
import { Dialog } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field, Textarea } from '@/components/ui/field';
import { usePermission } from '@/Hooks/usePermission';
import { apiMessage, pruneBody } from '../helpers';
import type { Role, RoleDetail } from '../types';
import RoleTemplates, {
  BLANK_START,
  suggestKey,
  useRoleTemplates,
  type StartingPoint,
} from './RoleTemplates';
import {
  PermissionPicker,
  PermissionPreview,
  blockedKeys,
  usePermissionCatalogue,
} from './PermissionMatrixDialog';

interface Props {
  open: boolean;
  /** Non-null = rename an existing custom role. */
  role: Role | null;
  /** Non-null = open straight into "duplicate this role" instead of the chooser. */
  duplicateOf?: Role | null;
  /** Existing roles: duplication sources, and the client-side key-clash check. */
  roles: Role[];
  onClose: () => void;
  onSaved: () => void;
}

/**
 * Create or rename a custom role.
 *
 * Creating is a two-step flow: choose a starting point (a seeded template, a
 * copy of any existing role, or nothing at all), then adjust every one of the
 * ~100 permission keys before saving. The role is written in ONE request —
 * `POST /roles` carries `permissionKeys` — so a set the escalation guard
 * refuses cannot leave a named role behind with nothing in it.
 *
 * A template is copied, never linked. That is stated on screen the moment one
 * is chosen, because the natural expectation is the opposite: people assume a
 * "template" keeps updating them, and discovering otherwise months later is the
 * kind of surprise that costs a venue its permission hygiene.
 *
 * The key is the stable handle other code matches on, so it is set once at
 * creation and never edited afterwards.
 */
export default function RoleFormDialog({
  open,
  role,
  duplicateOf,
  roles,
  onClose,
  onSaved,
}: Props) {
  const { t } = useTranslation();
  const { can } = usePermission();
  const isEdit = Boolean(role);

  const [start, setStart] = useState<StartingPoint | null>(null);
  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [dropped, setDropped] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const canGrant = useCallback((permissionKey: string) => can(permissionKey), [can]);
  const wantsChooser = open && !isEdit;
  const { templates, loading: templatesLoading, error: templatesError } =
    useRoleTemplates(wantsChooser);
  const { catalogue, loading: catalogueLoading } = usePermissionCatalogue(wantsChooser);

  const takenKeys = useMemo(
    () => new Set(roles.filter((r) => r.id !== role?.id).map((r) => r.key)),
    [roles, role],
  );

  /** `CASHIER` → `CASHIER_COPY`, then `_2`, `_3`… until it is free. */
  const freeKey = useCallback(
    (base: string) => {
      const root = suggestKey(base) || 'CUSTOM_ROLE';
      if (!takenKeys.has(root)) return root;
      for (let i = 2; i < 100; i += 1) {
        const candidate = `${root}_${i}`;
        if (!takenKeys.has(candidate)) return candidate;
      }
      return root;
    },
    [takenKeys],
  );

  /**
   * Applying a starting point drops anything the actor cannot hand out. The
   * server would refuse the whole set otherwise, and silently pre-ticking a box
   * that guarantees a 403 is worse than saying which ones were left out.
   */
  const applyStart = useCallback(
    (picked: StartingPoint) => {
      const grantable = picked.permissionKeys.filter(canGrant);
      setStart(picked);
      setSelected(new Set(grantable));
      setDropped(picked.permissionKeys.length - grantable.length);
      setErrors({});
      if (picked.kind === 'blank') {
        setKey('');
        setName('');
      } else {
        setKey(freeKey(`${picked.key}_COPY`));
        setName(t('settings.roles.duplicateOf', { name: picked.name }));
      }
      setDescription('');
    },
    [canGrant, freeKey, t],
  );

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setDropped(0);
    if (role) {
      setStart(null);
      setKey(role.key);
      setName(role.name);
      setDescription(role.description ?? '');
      setSelected(new Set());
      return;
    }
    setStart(null);
    setKey('');
    setName('');
    setDescription('');
    setSelected(new Set());
  }, [open, role]);

  /**
   * Opened from the row's Duplicate action — skip the chooser, load the source.
   * Applied once per open: `roles` gets a new identity whenever the list behind
   * the dialog reloads, and re-running this would silently wipe edits in
   * progress and put the pristine copy back.
   */
  const appliedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!open) appliedFor.current = null;
  }, [open]);

  useEffect(() => {
    if (!open || role || !duplicateOf) return;
    if (appliedFor.current === duplicateOf.id) return;
    appliedFor.current = duplicateOf.id;
    let cancelled = false;
    api
      .get<RoleDetail>(`/roles/${duplicateOf.id}`)
      .then((res) => {
        if (cancelled) return;
        applyStart({
          kind: 'role',
          key: duplicateOf.key,
          name: duplicateOf.name,
          permissionKeys: res.data.permissionKeys ?? [],
        });
      })
      .catch((error) => toast.error(apiMessage(error, t('settings.errors.loadFailed'))));
    return () => {
      cancelled = true;
    };
  }, [open, role, duplicateOf, applyStart, t]);

  const blocked = useMemo(() => blockedKeys(selected, canGrant), [selected, canGrant]);

  const submit = async () => {
    const next: Record<string, string> = {};
    if (!name.trim()) next.name = t('settings.validation.required');
    if (!isEdit) {
      if (!/^[A-Z][A-Z0-9_]*$/.test(key.trim())) next.key = t('settings.validation.roleKeyFormat');
      else if (takenKeys.has(key.trim())) next.key = t('settings.roles.keyTaken');
    }
    setErrors(next);
    if (Object.keys(next).length) return;

    setSaving(true);
    try {
      if (role) {
        await api.patch(`/roles/${role.id}`, pruneBody({ name: name.trim(), description }));
        toast.success(t('settings.roles.updated'));
      } else {
        await api.post(
          '/roles',
          // The permission set rides along with the create, so a template lands
          // in one round trip rather than create-then-permission.
          pruneBody({
            key: key.trim(),
            name: name.trim(),
            description,
            permissionKeys: [...selected],
          }),
        );
        toast.success(t('settings.roles.created'));
      }
      onSaved();
      onClose();
    } catch (error) {
      toast.error(apiMessage(error, t('settings.errors.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  /** Back to the chooser, with nothing from the abandoned starting point left. */
  const backToChooser = () => {
    applyStart(BLANK_START);
    setStart(null);
  };

  const choosing = !isEdit && start === null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={isEdit ? t('settings.roles.editTitle') : t('settings.roles.createTitle')}
      description={choosing ? t('settings.roles.startingPoint') : t('settings.roles.formHint')}
      className={choosing || !isEdit ? 'max-w-4xl' : undefined}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          {!choosing && (
            <Button
              onClick={() => void submit()}
              disabled={saving || blocked.length > 0 || (!isEdit && catalogueLoading)}
            >
              {saving ? t('settings.common.saving') : t('common.save')}
            </Button>
          )}
        </>
      }
    >
      {choosing ? (
        <RoleTemplates
          templates={templates}
          templatesLoading={templatesLoading}
          templatesError={templatesError}
          roles={roles}
          onPick={applyStart}
        />
      ) : (
        <div className="space-y-4">
          {!isEdit && start && start.kind !== 'blank' && (
            <div className="rounded-lg border border-sky-200 dark:border-sky-900 bg-sky-50 dark:bg-sky-950/50 p-3 text-sm text-sky-900 dark:text-sky-200">
              <p className="flex items-center gap-2 font-medium">
                <Link2Off className="h-4 w-4 shrink-0" />
                {t('settings.roles.copyNoticeTitle')}
              </p>
              <p className="mt-1">{t('settings.roles.copyNotice', { name: start.name })}</p>
              {dropped > 0 && (
                <p className="mt-1 flex items-start gap-1 text-amber-800 dark:text-amber-300">
                  <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  {t('settings.roles.droppedFromSource', { n: dropped })}
                </p>
              )}
              <Button
                size="sm"
                variant="outline"
                className="mt-2"
                onClick={backToChooser}
              >
                {t('settings.roles.changeStartingPoint')}
              </Button>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              label={t('settings.roles.key')}
              hint={t('settings.roles.keyHint')}
              error={errors.key}
              required={!isEdit}
            >
              <Input
                value={key}
                dir="ltr"
                className="text-start"
                disabled={isEdit}
                onChange={(e) => setKey(e.target.value.toUpperCase())}
              />
            </Field>
            <Field label={t('settings.roles.name')} required error={errors.name}>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
          </div>
          <Field label={t('settings.roles.description')}>
            <Textarea rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          </Field>

          {!isEdit && (
            <>
              <section className="space-y-2">
                <h4 className="text-sm font-semibold">{t('settings.roles.permissionsSection')}</h4>
                <PermissionPicker
                  catalogue={catalogue}
                  selected={selected}
                  onChange={setSelected}
                  canGrant={canGrant}
                />
              </section>

              <section className="rounded-lg border bg-[var(--muted)]/30 p-3">
                <h4 className="mb-2 text-sm font-semibold">{t('settings.roles.previewTitle')}</h4>
                <PermissionPreview catalogue={catalogue} selected={selected} />
              </section>
            </>
          )}
        </div>
      )}
    </Dialog>
  );
}
